import {ObjectId} from 'bson';
import {ensure, assertNever} from './util';
import {
  isAiFieldDefinition,
  isCustomerAnalysisField,
} from './fields/enrichment';
import {Agent} from './agent';
import {FieldDefinition} from './fields/types';

const RoleCountCreditCost = 3;
const ResearcherCreditCost = 3;
const BeastModeCreditCost = 9;
const BasicAnalystCreditCost = 1;
const TeamSizeEstimatorCreditCost = 5;

export function getFieldCreditCost(field: FieldDefinition) {
  const {type} = field;

  switch (type) {
    case 'crm':
      return 0;

    case 'intent':
      return 0;

    case 'roleCount':
      return RoleCountCreditCost;

    case 'ai':
      switch (field.config.fieldType) {
        case 'legacyResearch':
          return field.config.mode === 'beast'
            ? BeastModeCreditCost
            : ResearcherCreditCost;

        case 'research':
          return ResearcherCreditCost;

        case 'teamSize':
        case 'legacyTeamSize':
          return TeamSizeEstimatorCreditCost;

        default:
          return BasicAnalystCreditCost;
      }

    default:
      assertNever(type);
  }
}

// recursively calculates how many credits a field enrichment costs
export function getRecursiveFieldCreditCost({
  field,
  fields = [],
}: {
  field: FieldDefinition;
  fields: FieldDefinition[] | undefined;
}): number {
  let recursiveCost = 0;
  if (field.type === 'ai') {
    recursiveCost = field.config.analysisFields.reduce(
      (total, currentField) =>
        isCustomerAnalysisField(currentField)
          ? total +
            getRecursiveFieldCreditCost({
              field: ensure(
                fields.find((field) => field.id.equals(currentField.fieldId))
              ),
              fields,
            })
          : total,
      0
    );
  }

  return recursiveCost + getFieldCreditCost(field);
}

export function getCreditsNeededForEnrichment({
  numAccounts,
  fieldId,
  fields = [],
}: {
  fieldId: ObjectId;
  numAccounts: number;
  fields: FieldDefinition[] | undefined;
}) {
  const field = ensure(fields.find((field) => field.id.equals(fieldId)));

  return (
    getRecursiveFieldCreditCost({
      field,
      fields,
    }) * numAccounts
  );
}

export const getAgentCreditCost = (
  agentId: ObjectId,
  fieldDefinitions: FieldDefinition[] = []
) => {
  const fields = fieldDefinitions
    .filter(isAiFieldDefinition)
    .filter((field) => field.agentId.equals(agentId));

  if (fields.length === 0) {
    return undefined;
  }

  return fields.reduce((total, field) => total + getFieldCreditCost(field), 0);
};

export const getAgentCreditEstimateCost = (agentType: Agent['type']) => {
  return {
    analyst: BasicAnalystCreditCost,
    legacyResearch: ResearcherCreditCost,
    legacyResearchAndAnalyst: ResearcherCreditCost + BasicAnalystCreditCost,
    legacyTeamSize: TeamSizeEstimatorCreditCost,
    research: ResearcherCreditCost,
    researchAndAnalyst: ResearcherCreditCost + BasicAnalystCreditCost,
    teamSize: TeamSizeEstimatorCreditCost,
  }[agentType];
};
